import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';

type ChatbotValues = Record<string, string | null>;

const PROVIDERS = [
  { value: '', label: 'Por defecto (configuración del servidor)' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'ollama', label: 'Ollama (local)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'groq', label: 'Groq' },
  { value: 'mistral', label: 'Mistral' },
  { value: 'deepseek', label: 'DeepSeek' },
];

export function ChatbotConfig() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => unwrap<Record<string, ChatbotValues>>(api.get('/settings')),
  });

  const [values, setValues] = useState<ChatbotValues>({});
  useEffect(() => {
    if (data?.chatbot) setValues(data.chatbot);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put('/settings', { group: 'chatbot', values }),
    onSuccess: () => {
      toast.success('Configuración del chatbot guardada');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  function set(key: string, value: string) {
    setValues((v) => ({ ...v, [key]: value }));
  }

  function bool(key: string): boolean {
    return values[key] === 'true' || values[key] === '1';
  }

  if (isLoading) {
    return <div className="p-4 text-sm text-neutral-500">Cargando configuración…</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Apariencia y mensajes</CardTitle>
          <CardDescription>Lo que ve el visitante en el sitio.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="cb-enabled">Activar chatbot en el sitio</Label>
            <select
              id="cb-enabled"
              value={bool('chatbot_enabled') ? 'true' : 'false'}
              onChange={(e) => set('chatbot_enabled', e.target.value)}
              className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
            >
              <option value="true">Activado</option>
              <option value="false">Desactivado</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-name">Nombre del asistente</Label>
            <Input id="cb-name" value={values.chatbot_name ?? ''} onChange={(e) => set('chatbot_name', e.target.value)} placeholder="Asistente Sytel" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cb-welcome">Mensaje de bienvenida</Label>
            <Textarea id="cb-welcome" rows={2} value={values.chatbot_welcome ?? ''} onChange={(e) => set('chatbot_welcome', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-placeholder">Texto del campo de entrada</Label>
            <Input id="cb-placeholder" value={values.chatbot_placeholder ?? ''} onChange={(e) => set('chatbot_placeholder', e.target.value)} placeholder="Escribe tu pregunta…" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-color">Color principal</Label>
            <div className="flex items-center gap-2">
              <input
                id="cb-color"
                type="color"
                value={values.chatbot_color || '#AD026A'}
                onChange={(e) => set('chatbot_color', e.target.value)}
                className="h-9 w-12 cursor-pointer rounded-md border border-neutral-200"
              />
              <Input value={values.chatbot_color ?? ''} onChange={(e) => set('chatbot_color', e.target.value)} placeholder="#AD026A" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-position">Posición</Label>
            <select
              id="cb-position"
              value={values.chatbot_position || 'right'}
              onChange={(e) => set('chatbot_position', e.target.value)}
              className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
            >
              <option value="right">Abajo a la derecha</option>
              <option value="left">Abajo a la izquierda</option>
            </select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cb-quick">Respuestas rápidas (una por línea)</Label>
            <Textarea id="cb-quick" rows={3} value={values.chatbot_quick_replies ?? ''} onChange={(e) => set('chatbot_quick_replies', e.target.value)} placeholder={'¿Qué servicios ofrecéis?\nQuiero una propuesta'} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inteligencia (LLM + RAG)</CardTitle>
          <CardDescription>Modelo que responde y prompt del sistema. El contexto se construye con la base de conocimiento.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="cb-provider">Proveedor del modelo</Label>
            <select id="cb-provider" value={values.chatbot_provider ?? ''} onChange={(e) => set('chatbot_provider', e.target.value)} className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm">
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-model">Modelo</Label>
            <Input id="cb-model" value={values.chatbot_model ?? ''} onChange={(e) => set('chatbot_model', e.target.value)} placeholder="openai/gpt-4o-mini, llama3.1, …" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-embprov">Proveedor de embeddings (RAG)</Label>
            <select id="cb-embprov" value={values.chatbot_embed_provider ?? ''} onChange={(e) => set('chatbot_embed_provider', e.target.value)} className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm">
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-embmodel">Modelo de embeddings</Label>
            <Input id="cb-embmodel" value={values.chatbot_embed_model ?? ''} onChange={(e) => set('chatbot_embed_model', e.target.value)} placeholder="text-embedding-3-small, nomic-embed-text…" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cb-system">Prompt del sistema</Label>
            <Textarea id="cb-system" rows={5} value={values.chatbot_system_prompt ?? ''} onChange={(e) => set('chatbot_system_prompt', e.target.value)} placeholder="Deja vacío para usar el prompt por defecto. Usa {site_name} para el nombre del sitio." />
            <p className="text-xs text-neutral-400">Vacío = prompt por defecto. Variable disponible: <code>{'{site_name}'}</code>.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Memoria del asistente</CardTitle>
          <CardDescription>
            Lo que el bot <strong>siempre sabe</strong> (empresa, servicios, tono, reglas). Se incluye en cada respuesta,
            así conoce a la empresa sin depender de la búsqueda semántica. Admite Markdown. Variable: <code>{'{site_name}'}</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <Label htmlFor="cb-memory">Memoria / base de conocimiento (memory.md)</Label>
            <Textarea
              id="cb-memory"
              rows={14}
              className="font-mono text-xs"
              value={values.chatbot_memory ?? ''}
              onChange={(e) => set('chatbot_memory', e.target.value)}
              placeholder={'# Sobre la empresa\nQué hacéis y a quién os dirigís.\n\n## Servicios\n- Lista de servicios o productos principales...\n\n## Tono\nCercano y profesional. Responde en español.\n\n## Reglas\n- No inventes precios. Deriva a un especialista.'}
            />
            <p className="text-xs text-neutral-400">Consejo: estructura con títulos (##) servicios, datos de contacto, FAQs y reglas. Cuanto más claro, mejor responde.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Comportamiento</CardTitle>
          <CardDescription>Cuánto recuerda de la conversación, cuánto contexto usa y su estilo de respuesta.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="cb-history">Memoria (mensajes recordados)</Label>
            <Input id="cb-history" type="number" min={0} max={30} value={values.chatbot_history_messages ?? ''} onChange={(e) => set('chatbot_history_messages', e.target.value)} placeholder="8" />
            <p className="text-xs text-neutral-400">Mensajes anteriores que recuerda en la conversación. 0 = sin memoria.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-passages">Pasajes de contexto (RAG)</Label>
            <Input id="cb-passages" type="number" min={0} max={12} value={values.chatbot_context_passages ?? ''} onChange={(e) => set('chatbot_context_passages', e.target.value)} placeholder="4" />
            <p className="text-xs text-neutral-400">Fragmentos de la base de conocimiento que se inyectan. 0 = sin RAG.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-temp">Creatividad (temperatura)</Label>
            <Input id="cb-temp" type="number" min={0} max={2} step={0.1} value={values.chatbot_temperature ?? ''} onChange={(e) => set('chatbot_temperature', e.target.value)} placeholder="0.5" />
            <p className="text-xs text-neutral-400">0 = preciso y consistente · 1+ = más creativo e impredecible.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-maxtokens">Longitud máx. de respuesta (tokens)</Label>
            <Input id="cb-maxtokens" type="number" min={64} max={4000} step={32} value={values.chatbot_max_tokens ?? ''} onChange={(e) => set('chatbot_max_tokens', e.target.value)} placeholder="800" />
            <p className="text-xs text-neutral-400">Tope de longitud de cada respuesta. Más alto = respuestas potencialmente más largas.</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cb-autoclose">Cerrar la conversación al resolverse</Label>
            <select id="cb-autoclose" value={values.chatbot_auto_close === 'false' ? 'false' : 'true'} onChange={(e) => set('chatbot_auto_close', e.target.value)} className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm">
              <option value="true">Activado</option>
              <option value="false">Desactivado</option>
            </select>
            <p className="text-xs text-neutral-400">El asistente se despide y cierra el chat cuando ve que las dudas están resueltas. El visitante puede iniciar una nueva conversación cuando quiera.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Captura de leads</CardTitle>
          <CardDescription>Cuando el visitante deja email o teléfono se crea un lead y se reenvía al CRM (si está activo en Integraciones).</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="cb-lead">Capturar leads en el chat</Label>
            <select id="cb-lead" value={bool('chatbot_lead_capture') ? 'true' : 'false'} onChange={(e) => set('chatbot_lead_capture', e.target.value)} className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm">
              <option value="true">Activado</option>
              <option value="false">Desactivado</option>
            </select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cb-offline">Mensaje de respaldo (sin LLM disponible)</Label>
            <Textarea id="cb-offline" rows={2} value={values.chatbot_offline_message ?? ''} onChange={(e) => set('chatbot_offline_message', e.target.value)} placeholder="Opcional. Se muestra si el modelo no está disponible." />
          </div>
        </CardContent>
      </Card>

      <div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          <Save className="size-4" /> {save.isPending ? 'Guardando…' : 'Guardar configuración'}
        </Button>
      </div>
    </div>
  );
}
